import React, { useState, useCallback, useRef, useEffect } from 'react';
import { hexToRgb, rgbToHex, hexToHsl, hslToHex, isValidHex, normalizeHex } from './colorUtils';

/**
 * SolidColorPicker - A modern color selection UI for solid colors
 *
 * @param {Object} props
 * @param {string} props.color - Current hex color (e.g., '#4f46e5')
 * @param {number} props.alpha - Current alpha (0-100)
 * @param {Function} props.onChange - Callback when color changes (color: string, alpha: number) => void
 */
export default function SolidColorPicker({ color, alpha, onChange }) {
  // Color input mode: 'hex', 'rgb', 'hsl'
  const [inputMode, setInputMode] = useState('hex');

  // Local hex input state for free typing (null = use prop value)
  const [hexInputValue, setHexInputValue] = useState(null);

  // Parse initial color to HSL for the picker
  const getHslFromColor = useCallback((hexColor) => {
    const hsl = hexToHsl(hexColor);
    return hsl || { h: 0, s: 100, l: 50 };
  }, []);

  // Internal HSL state for the picker
  const [hsl, setHsl] = useState(() => getHslFromColor(color));

  // Refs for drag handling
  const satBrightRef = useRef(null);
  const hueRef = useRef(null);
  const alphaRef = useRef(null);
  const isDraggingRef = useRef(null);

  // Update internal HSL when color prop changes externally
  useEffect(() => {
    const newHsl = getHslFromColor(color);
    // Only update if significantly different to avoid loops
    if (
      Math.abs(newHsl.h - hsl.h) > 1 ||
      Math.abs(newHsl.s - hsl.s) > 1 ||
      Math.abs(newHsl.l - hsl.l) > 1
    ) {
      setHsl(newHsl);
    }
  }, [color, getHslFromColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convert HSL to saturation/brightness for the picker
  // The picker uses HSB/HSV model where:
  // - X axis = Saturation (0-100)
  // - Y axis = Brightness/Value (100-0, top to bottom)
  // HSL to HSV conversion
  const hslToHsv = useCallback((h, s, l) => {
    const sNorm = s / 100;
    const lNorm = l / 100;
    const v = lNorm + sNorm * Math.min(lNorm, 1 - lNorm);
    const sv = v === 0 ? 0 : 2 * (1 - lNorm / v);
    return { h, s: sv * 100, v: v * 100 };
  }, []);

  const hsvToHsl = useCallback((h, s, v) => {
    const sNorm = s / 100;
    const vNorm = v / 100;
    const l = vNorm * (1 - sNorm / 2);
    const sl = l === 0 || l === 1 ? 0 : (vNorm - l) / Math.min(l, 1 - l);
    return { h, s: sl * 100, l: l * 100 };
  }, []);

  // Get current HSV for picker position
  const hsv = hslToHsv(hsl.h, hsl.s, hsl.l);

  // Handle saturation/brightness area interaction
  const handleSatBrightChange = useCallback(
    (clientX, clientY) => {
      const rect = satBrightRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

      const newSaturation = x * 100;
      const newValue = (1 - y) * 100;

      const newHsl = hsvToHsl(hsl.h, newSaturation, newValue);
      setHsl(newHsl);

      const newColor = hslToHex(newHsl.h, newHsl.s, newHsl.l);
      // Auto-set alpha to 100 when picking a color while alpha is 0 (transparent)
      onChange(newColor, alpha === 0 ? 100 : alpha);
    },
    [hsl.h, hsvToHsl, alpha, onChange]
  );

  // Handle hue slider interaction
  const handleHueChange = useCallback(
    (clientX) => {
      const rect = hueRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newHue = x * 360;

      const newHsl = { ...hsl, h: newHue };
      setHsl(newHsl);

      const newColor = hslToHex(newHsl.h, newHsl.s, newHsl.l);
      // Auto-set alpha to 100 when picking a color while alpha is 0 (transparent)
      onChange(newColor, alpha === 0 ? 100 : alpha);
    },
    [hsl, alpha, onChange]
  );

  // Handle alpha slider interaction
  const handleAlphaChange = useCallback(
    (clientX) => {
      const rect = alphaRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newAlpha = Math.round(x * 100);

      onChange(color, newAlpha);
    },
    [color, onChange]
  );

  // Mouse/touch event handlers
  const handleMouseDown = useCallback(
    (e, type) => {
      e.preventDefault();
      isDraggingRef.current = type;

      if (type === 'satbright') {
        handleSatBrightChange(e.clientX, e.clientY);
      } else if (type === 'hue') {
        handleHueChange(e.clientX);
      } else if (type === 'alpha') {
        handleAlphaChange(e.clientX);
      }
    },
    [handleSatBrightChange, handleHueChange, handleAlphaChange]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return;

      // Support both mouse and touch events
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      if (isDraggingRef.current === 'satbright') {
        handleSatBrightChange(clientX, clientY);
      } else if (isDraggingRef.current === 'hue') {
        handleHueChange(clientX);
      } else if (isDraggingRef.current === 'alpha') {
        handleAlphaChange(clientX);
      }
    },
    [handleSatBrightChange, handleHueChange, handleAlphaChange]
  );

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = null;
  }, []);

  // Add global mouse listeners for drag
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Handle touch events
  const handleTouchStart = useCallback(
    (e, type) => {
      e.preventDefault();
      const touch = e.touches[0];
      isDraggingRef.current = type;

      if (type === 'satbright') {
        handleSatBrightChange(touch.clientX, touch.clientY);
      } else if (type === 'hue') {
        handleHueChange(touch.clientX);
      } else if (type === 'alpha') {
        handleAlphaChange(touch.clientX);
      }
    },
    [handleSatBrightChange, handleHueChange, handleAlphaChange]
  );

  // Input field handlers
  const handleHexInput = useCallback(
    (e) => {
      let value = e.target.value;
      if (!value.startsWith('#')) {
        value = '#' + value;
      }

      // Always update local state so user can type freely
      setHexInputValue(value);

      // Only propagate valid hex colors upstream
      if (isValidHex(value)) {
        const normalized = normalizeHex(value);
        setHsl(getHslFromColor(normalized));
        // Auto-set alpha to 100 when typing a color while alpha is 0 (transparent)
        onChange(normalized, alpha === 0 ? 100 : alpha);
      }
    },
    [alpha, onChange, getHslFromColor]
  );

  // Clear local hex input when color changes from outside (canvas, sliders, presets)
  useEffect(() => {
    setHexInputValue(null);
  }, [color]);

  const handleRgbInput = useCallback(
    (channel, value) => {
      const rgb = hexToRgb(color) || { r: 0, g: 0, b: 0 };
      const numValue = Math.max(0, Math.min(255, parseInt(value, 10) || 0));

      const newRgb = { ...rgb, [channel]: numValue };
      const newColor = rgbToHex(newRgb.r, newRgb.g, newRgb.b);

      setHsl(getHslFromColor(newColor));
      // Auto-set alpha to 100 when typing a color while alpha is 0 (transparent)
      onChange(newColor, alpha === 0 ? 100 : alpha);
    },
    [color, alpha, onChange, getHslFromColor]
  );

  const handleHslInput = useCallback(
    (channel, value) => {
      let numValue = parseInt(value, 10) || 0;

      if (channel === 'h') {
        numValue = Math.max(0, Math.min(360, numValue));
      } else {
        numValue = Math.max(0, Math.min(100, numValue));
      }

      const newHsl = { ...hsl, [channel]: numValue };
      setHsl(newHsl);

      const newColor = hslToHex(newHsl.h, newHsl.s, newHsl.l);
      // Auto-set alpha to 100 when typing a color while alpha is 0 (transparent)
      onChange(newColor, alpha === 0 ? 100 : alpha);
    },
    [hsl, alpha, onChange]
  );

  const handleAlphaInput = useCallback(
    (e) => {
      const value = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
      onChange(color, value);
    },
    [color, onChange]
  );

  // Get current RGB values for inputs
  const rgb = hexToRgb(color) || { r: 0, g: 0, b: 0 };

  // Pure hue color for saturation/brightness background
  const pureHueColor = hslToHex(hsl.h, 100, 50);

  // Checkered pattern for transparency backgrounds
  const checkeredBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill-opacity='.1'%3E%3Crect x='0' y='0' width='8' height='8' fill='%23808080'/%3E%3Crect x='8' y='8' width='8' height='8' fill='%23808080'/%3E%3C/svg%3E")`;

  return (
    <div className="space-y-3">
      {/* Saturation/Brightness Area */}
      <div
        ref={satBrightRef}
        className="relative w-full h-40 rounded cursor-crosshair select-none"
        style={{
          backgroundColor: pureHueColor,
          backgroundImage: `
            linear-gradient(to bottom, rgba(0,0,0,0), #000000),
            linear-gradient(to right, #ffffff, rgba(255,255,255,0))
          `,
        }}
        onMouseDown={(e) => handleMouseDown(e, 'satbright')}
        onTouchStart={(e) => handleTouchStart(e, 'satbright')}
        role="slider"
        aria-label="Saturation and brightness"
        aria-valuenow={Math.round(hsv.s)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`Saturation ${Math.round(hsv.s)}%, Brightness ${Math.round(hsv.v)}%`}
        tabIndex={0}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 10 : 1;
          let newS = hsv.s;
          let newV = hsv.v;

          switch (e.key) {
            case 'ArrowRight':
              newS = Math.min(100, hsv.s + step);
              break;
            case 'ArrowLeft':
              newS = Math.max(0, hsv.s - step);
              break;
            case 'ArrowUp':
              newV = Math.min(100, hsv.v + step);
              break;
            case 'ArrowDown':
              newV = Math.max(0, hsv.v - step);
              break;
            default:
              return;
          }

          e.preventDefault();
          const newHsl = hsvToHsl(hsl.h, newS, newV);
          setHsl(newHsl);
          onChange(hslToHex(newHsl.h, newHsl.s, newHsl.l), alpha === 0 ? 100 : alpha);
        }}
      >
        {/* Position indicator */}
        <div
          className="absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 border-2 border-white rounded-full shadow-md pointer-events-none"
          style={{
            left: `${hsv.s}%`,
            top: `${100 - hsv.v}%`,
            backgroundColor: color,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      </div>

      {/* Hue Slider */}
      <div
        ref={hueRef}
        className="relative w-full h-3 rounded cursor-pointer select-none"
        style={{
          background:
            'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
        }}
        onMouseDown={(e) => handleMouseDown(e, 'hue')}
        onTouchStart={(e) => handleTouchStart(e, 'hue')}
        role="slider"
        aria-label="Hue"
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={Math.round(hsl.h)}
        tabIndex={0}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 10 : 1;
          let newHue = hsl.h;

          switch (e.key) {
            case 'ArrowRight':
              newHue = (hsl.h + step) % 360;
              break;
            case 'ArrowLeft':
              newHue = (hsl.h - step + 360) % 360;
              break;
            default:
              return;
          }

          e.preventDefault();
          const newHsl = { ...hsl, h: newHue };
          setHsl(newHsl);
          onChange(hslToHex(newHsl.h, newHsl.s, newHsl.l), alpha === 0 ? 100 : alpha);
        }}
      >
        {/* Hue handle */}
        <div
          className="absolute w-3 h-5 -translate-x-1/2 -translate-y-1/4 rounded border-2 border-white shadow-md pointer-events-none"
          style={{
            left: `${(hsl.h / 360) * 100}%`,
            backgroundColor: hslToHex(hsl.h, 100, 50),
            boxShadow: '0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      </div>

      {/* Alpha Slider */}
      <div
        ref={alphaRef}
        className="relative w-full h-3 rounded cursor-pointer select-none"
        style={{
          backgroundImage: checkeredBg,
          backgroundColor: '#fff',
        }}
        onMouseDown={(e) => handleMouseDown(e, 'alpha')}
        onTouchStart={(e) => handleTouchStart(e, 'alpha')}
        role="slider"
        aria-label="Opacity"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={alpha}
        tabIndex={0}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 10 : 1;
          let newAlpha = alpha;

          switch (e.key) {
            case 'ArrowRight':
              newAlpha = Math.min(100, alpha + step);
              break;
            case 'ArrowLeft':
              newAlpha = Math.max(0, alpha - step);
              break;
            default:
              return;
          }

          e.preventDefault();
          onChange(color, newAlpha);
        }}
      >
        {/* Alpha gradient overlay */}
        <div
          className="absolute inset-0 rounded pointer-events-none"
          style={{
            background: `linear-gradient(to right, transparent, ${color})`,
          }}
        />
        {/* Alpha handle */}
        <div
          className="absolute w-3 h-5 -translate-x-1/2 -translate-y-1/4 rounded border-2 border-white shadow-md pointer-events-none"
          style={{
            left: `${alpha}%`,
            backgroundColor: color,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      </div>

      {/* Preview and Input Fields */}
      <div className="flex items-start gap-3">
        {/* Preview Swatch */}
        <div
          className="w-10 h-10 rounded border border-gray-200 flex-shrink-0 overflow-hidden"
          style={{
            backgroundImage: checkeredBg,
            backgroundColor: '#fff',
          }}
        >
          <div
            className="w-full h-full"
            style={{
              backgroundColor: color,
              opacity: alpha / 100,
            }}
          />
        </div>

        {/* Input Fields */}
        <div className="flex-1 space-y-2">
          {/* Mode Tabs */}
          <div className="flex border border-gray-200 rounded overflow-hidden text-xs">
            {['hex', 'rgb', 'hsl'].map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setInputMode(mode)}
                className={`flex-1 px-2 py-1 uppercase font-medium transition-colors
                  ${
                    inputMode === mode
                      ? 'bg-gray-100 text-gray-900'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
              >
                {mode}
              </button>
            ))}
          </div>

          {/* Input Fields based on mode */}
          <div className="flex gap-2">
            {inputMode === 'hex' && (
              <>
                <div className="flex-1">
                  <input
                    type="text"
                    value={hexInputValue !== null ? hexInputValue : normalizeHex(color)}
                    onChange={handleHexInput}
                    onBlur={() => setHexInputValue(null)}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                    placeholder="#000000"
                  />
                  <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                    HEX
                  </label>
                </div>
                <div className="w-14">
                  <input
                    type="number"
                    value={alpha}
                    onChange={handleAlphaInput}
                    min={0}
                    max={100}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  />
                  <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                    A%
                  </label>
                </div>
              </>
            )}

            {inputMode === 'rgb' && (
              <>
                {['r', 'g', 'b'].map((channel) => (
                  <div key={channel} className="flex-1">
                    <input
                      type="number"
                      value={rgb[channel]}
                      onChange={(e) => handleRgbInput(channel, e.target.value)}
                      min={0}
                      max={255}
                      className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                    />
                    <label className="block text-[10px] text-gray-400 mt-0.5 text-center uppercase">
                      {channel}
                    </label>
                  </div>
                ))}
                <div className="w-12">
                  <input
                    type="number"
                    value={alpha}
                    onChange={handleAlphaInput}
                    min={0}
                    max={100}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  />
                  <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                    A%
                  </label>
                </div>
              </>
            )}

            {inputMode === 'hsl' && (
              <>
                <div className="flex-1">
                  <input
                    type="number"
                    value={Math.round(hsl.h)}
                    onChange={(e) => handleHslInput('h', e.target.value)}
                    min={0}
                    max={360}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  />
                  <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                    H
                  </label>
                </div>
                <div className="flex-1">
                  <input
                    type="number"
                    value={Math.round(hsl.s)}
                    onChange={(e) => handleHslInput('s', e.target.value)}
                    min={0}
                    max={100}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  />
                  <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                    S%
                  </label>
                </div>
                <div className="flex-1">
                  <input
                    type="number"
                    value={Math.round(hsl.l)}
                    onChange={(e) => handleHslInput('l', e.target.value)}
                    min={0}
                    max={100}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  />
                  <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                    L%
                  </label>
                </div>
                <div className="w-12">
                  <input
                    type="number"
                    value={alpha}
                    onChange={handleAlphaInput}
                    min={0}
                    max={100}
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  />
                  <label className="block text-[10px] text-gray-400 mt-0.5 text-center">
                    A%
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
